// Clinical measurement/scoring sections, ported from source lines 5040-6079:
// vital signs, physical exam (17 systems), caliper/CT-scan tumor measurement,
// TNM staging, blood panel, cytokines, CORQ QoL scale.
import { escapeHtml } from "./dom.js";
import { renderField } from "./section-fields.js";
import {
  VITAL_SIGNS, PHYSICAL_SYSTEMS, PHYSICAL_RESULT_CODES, BLOOD_PANEL, CYTOKINES,
  TNM_T, TNM_N, TNM_M, STAGES, CORQ_ITEMS, CORQ_SECTIONS, computeBloodFlag, tumorVolume,
} from "@woof/shared";

export function renderVitalSection(v: Record<string, any>, writable: boolean): string {
  // Per-sign comment uses the same flat "<key>_comment" sibling-key
  // convention as the blood panel (visits-clinical.ts renderBloodSection),
  // not the physical section's "<key>.comment" dotted convention - that one
  // is a pre-existing bug in section-fields.ts's readFields(), which returns
  // a literal dotted top-level key instead of a nested object.
  const rows = VITAL_SIGNS.map((vs) => {
    const attrs: string[] = [];
    if (vs.min !== undefined) attrs.push(`min="${vs.min}"`);
    if (vs.max !== undefined) attrs.push(`max="${vs.max}"`);
    if (vs.step !== undefined) attrs.push(`step="${vs.step}"`);
    const comment = v[`${vs.key}_comment`] || "";
    return `<tr><td>${vs.label}</td><td>${vs.unit}</td><td>${renderField("", vs.key, "number", v[vs.key], writable, { attr: attrs.join(" ") })}</td>
      <td><input type="text" data-fkey="${vs.key}_comment" value="${escapeHtml(comment)}" ${writable ? "" : "disabled"} placeholder="Commentaire"></td></tr>`;
  }).join("");
  return `<p class="field-hint">Score ECOG modifié pour chiens : 0 = activité normale … 5 = état moribond. Critère d'inclusion : ECOG ≤ 1.</p>
    <table class="data-table"><thead><tr><th>Paramètre</th><th>Unité</th><th>Valeur</th><th>Commentaire</th></tr></thead><tbody>${rows}</tbody></table>`;
}

export function renderPhysicalSection(v: Record<string, any>, writable: boolean): string {
  let csMissing = 0, ncsMissing = 0;
  const rows = PHYSICAL_SYSTEMS.map((s) => {
    const sys = v[s.key] || {};
    const result = sys.result || "Normal";
    const comment = sys.comment || "";
    const isCS = result === "Anormal CS";
    const isNCS = result === "Anormal NCS";
    const missing = (isCS || isNCS) && !comment.trim();
    if (missing && isCS) csMissing++;
    if (missing && isNCS) ncsMissing++;
    const opts = PHYSICAL_RESULT_CODES.map((o) => `<option value="${o}" ${result === o ? "selected" : ""}>${o}</option>`).join("");
    const rowBg = isCS && missing ? "background:#fef2f2" : isNCS && missing ? "background:#fffbeb" : "";
    const alert = isCS && missing
      ? `<div class="field-warning">⛔ Commentaire OBLIGATOIRE pour "Anormal CS"</div>`
      : isNCS && missing
        ? `<div class="field-warning" style="color:#92400e">⚠️ Commentaire recommandé</div>`
        : "";
    return `<tr ${rowBg ? `style="${rowBg}"` : ""}>
      <td>${escapeHtml(s.label)}</td>
      <td><select data-fkey="${s.key}.result" ${writable ? "" : "disabled"}>${opts}</select></td>
      <td><input type="text" data-fkey="${s.key}.comment" value="${escapeHtml(comment)}" ${writable ? "" : "disabled"}
            placeholder="${isCS ? "Commentaire OBLIGATOIRE" : isNCS ? "Commentaire recommandé" : "Commentaire si anormal"}"
            class="${missing && isCS ? "field-error" : ""}">${alert}</td>
    </tr>`;
  }).join("");
  const globalAlert = csMissing > 0
    ? `<div class="alert-sae" style="margin-bottom:12px"><strong>⛔ ${csMissing} anomalie(s) CS sans commentaire.</strong> Ces écarts peuvent constituer un Événement Indésirable à déclarer.</div>`
    : ncsMissing > 0
      ? `<div class="alert-pv" style="margin-bottom:12px;background:#fffbeb;border-color:#fde68a;color:#92400e"><strong>${ncsMissing} anomalie(s) NCS sans commentaire.</strong></div>`
      : "";
  return `<p class="field-hint">NCS = Non Cliniquement Significatif · CS = Cliniquement Significatif · ND = Not Done. <strong>Anormal CS → commentaire obligatoire.</strong></p>
    ${globalAlert}
    <table class="data-table"><thead><tr><th>Système</th><th>Résultat</th><th>Commentaire</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function measurementSection(v: Record<string, any>, writable: boolean, unitLabel: string): string {
  const L = parseFloat(v.length), W = parseFloat(v.width), D = parseFloat(v.depth);
  const vol = tumorVolume(L, W, D);
  const volStr = vol !== null ? `${vol.toFixed(1)} cm³` : "—";
  const dimWarn = !Number.isNaN(L) && !Number.isNaN(W) && L > 0 && W > 0 && L < W
    ? `<div class="field-warning">⚠️ Incohérence : Rostro-Caudal (RCd) doit être ≥ Dorso-Ventral (DV).</div>`
    : "";
  return `<div class="form-grid">
      ${renderField("Date mesure", "date", "date", v.date, writable, { required: true })}
      ${renderField("Rostro-Caudal (RCd) — Longueur", "length", "number", v.length, writable, { unit: "cm", attr: 'step="0.1" min="0"' })}
      ${renderField("Dorso-Ventral (DV) — Hauteur", "width", "number", v.width, writable, { unit: "cm", attr: 'step="0.1" min="0"' })}
      ${renderField("Latéro-Latéral (LL) — Largeur", "depth", "number", v.depth, writable, { unit: "cm", attr: 'step="0.1" min="0"' })}
      <div class="form-group"><label>Volume calculé (cm³)</label><input type="text" value="${volStr}" disabled style="background:#f3f4f6;font-weight:600;color:#1e3a8a">
        <p class="field-hint">V = RCd × DV × LL × 0,524</p></div>
    </div>
    ${dimWarn}
    <div class="form-grid" style="margin-top:12px">${renderField("Notes", "notes", "textarea", v.notes, writable)}</div>`;
}

export function renderCaliperSection(v: Record<string, any>, writable: boolean): string {
  return measurementSection(v, writable, "cm");
}
export function renderCtscanSection(v: Record<string, any>, writable: boolean): string {
  return measurementSection(v, writable, "cm") + `<div class="form-grid" style="margin-top:12px">
    ${renderField("Injection produit de contraste", "contrast", "select", v.contrast, writable, { choices: ["Oui", "Non"] })}
  </div>`;
}

export function renderTnmSection(v: Record<string, any>, caliper: Record<string, any>, writable: boolean): string {
  const opts = (val: string, list: string[]) =>
    `<option value="" ${!val ? "selected" : ""}>— Sélectionner —</option>` + list.map((o) => `<option value="${o}" ${val === o ? "selected" : ""}>${o}</option>`).join("");

  let suggestedStage: string | null = null;
  const reasons: string[] = [];
  if (v.T && v.N && v.M) {
    if (v.M === "M1") { suggestedStage = "IV"; reasons.push("M1 → Stade IV"); }
    else if (v.N === "N1" || v.N === "N2") { suggestedStage = "III"; reasons.push(`${v.N} (atteinte ganglionnaire) → Stade III`); }
    else if (v.T === "T3") { suggestedStage = "III"; reasons.push("T3 (> 4 cm) → Stade III"); }
    else if (v.T === "T2") { suggestedStage = "II"; reasons.push("T2 (2-4 cm) → Stade II"); }
    else if (v.T === "T1" || v.T === "T0") { suggestedStage = "I"; reasons.push(`${v.T} → Stade I`); }
  }
  const protocolViolation = v.M === "M1";

  const lenCm = parseFloat(caliper?.length);
  let tCoherenceWarn = "";
  if (!Number.isNaN(lenCm) && lenCm > 0 && v.T) {
    const expectedT = lenCm <= 2 ? "T1" : lenCm <= 4 ? "T2" : "T3";
    if (expectedT !== v.T && v.T !== "T0") {
      tCoherenceWarn = `<div class="field-warning">⚠️ Incohérence T vs taille mesurée (${lenCm} cm suggère ${expectedT}), mais ${v.T} sélectionné.</div>`;
    }
  }

  return `
    ${protocolViolation ? `<div class="alert-sae" style="margin-bottom:12px"><strong>⛔ M1 détecté : les métastases à distance sont un critère d'EXCLUSION du protocole WOOF-01.</strong></div>` : ""}
    <div class="form-grid">
      <div class="form-group"><label>T (Tumeur)</label><select data-fkey="T" ${writable ? "" : "disabled"}>${opts(v.T, TNM_T)}</select></div>
      <div class="form-group"><label>N (Ganglions)</label><select data-fkey="N" ${writable ? "" : "disabled"}>${opts(v.N, TNM_N)}</select></div>
      <div class="form-group"><label>M (Métastases)</label><select data-fkey="M" ${writable ? "" : "disabled"}>${opts(v.M, TNM_M)}</select></div>
      <div class="form-group"><label>Stade</label><select data-fkey="stage" ${writable ? "" : "disabled"}>${opts(v.stage, STAGES)}</select></div>
    </div>
    ${tCoherenceWarn}
    ${suggestedStage ? `<div class="alert alert-info" style="margin-top:10px">💡 Stade suggéré : <strong>${suggestedStage}</strong> (${reasons.join("; ")}). ${v.stage !== suggestedStage ? "Le stade sélectionné diffère de la suggestion — vérifier." : ""}</div>` : ""}`;
}

export function renderBloodSection(v: Record<string, any>, writable: boolean): string {
  const csRows: string[] = [];
  const rows = BLOOD_PANEL.map((t) => {
    const val = v[t.key];
    const flag = computeBloodFlag(t, val);
    const severity = v[`${t.key}_severity`] || "";
    const comment = v[`${t.key}_comment`] || "";
    const needsQualifier = flag === "Low" || flag === "High";
    const missingComment = severity === "CS" && !comment.trim();
    if (missingComment) csRows.push(t.label);
    const flagBadge = flag === "Low" ? '<span class="badge badge-danger">↓ LLN</span>' : flag === "High" ? '<span class="badge badge-danger">↑ ULN</span>' : flag === "Normal" ? '<span class="badge badge-success">N</span>' : "";
    return `<tr ${missingComment ? 'style="background:#fef2f2"' : ""}>
      <td>${t.label}</td><td>${t.unit}</td>
      <td><input type="number" step="0.01" data-fkey="${t.key}" value="${val ?? ""}" ${writable ? "" : "disabled"}></td>
      <td>${flagBadge}</td>
      <td>${needsQualifier ? `<select data-fkey="${t.key}_severity" ${writable ? "" : "disabled"}><option value="">—</option><option value="NCS" ${severity === "NCS" ? "selected" : ""}>NCS</option><option value="CS" ${severity === "CS" ? "selected" : ""}>CS</option></select>` : ""}</td>
      <td><input type="text" data-fkey="${t.key}_comment" value="${escapeHtml(comment)}" ${writable ? "" : "disabled"} placeholder="${severity === "CS" ? "Commentaire obligatoire" : ""}" class="${missingComment ? "field-error" : ""}"></td>
    </tr>`;
  }).join("");

  return `
    <p class="field-hint">Flag calculé automatiquement selon bornes LLN/ULN. Écart hors bornes → qualifier NCS/CS. <strong>CS → commentaire obligatoire</strong> (possible EI à déclarer).</p>
    ${csRows.length ? `<div class="alert-sae" style="margin-bottom:12px"><strong>⛔ Commentaire manquant pour : ${csRows.join(", ")}</strong></div>` : ""}
    <div class="form-group" style="max-width:220px"><label>Date de prélèvement</label><input type="date" data-fkey="sample_date" value="${v.sample_date || ""}" ${writable ? "" : "disabled"}></div>
    <table class="data-table" style="margin-top:8px">
      <thead><tr><th>Analyte</th><th>Unité</th><th>Valeur</th><th>Flag</th><th>Qualif.</th><th>Commentaire</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
}

export function renderCytokinesSection(v: Record<string, any>, writable: boolean): string {
  const rows = CYTOKINES.map((c) => `<tr><td>${c.label}</td><td>${c.unit}</td><td>${renderField("", c.key, "number", v[c.key], writable)}</td></tr>`).join("");
  return `<div class="form-group" style="max-width:220px"><label>Date de prélèvement</label><input type="date" data-fkey="sample_date" value="${v.sample_date || ""}" ${writable ? "" : "disabled"}></div>
    <table class="data-table" style="margin-top:8px"><thead><tr><th>Cytokine</th><th>Unité</th><th>Valeur</th></tr></thead><tbody>${rows}</tbody></table>`;
}

export function renderCorqSection(v: Record<string, any>, writable: boolean): string {
  const items = v.items || {};
  const sectionTotal = (sk: string) => {
    const inSection = CORQ_ITEMS.filter((it) => it.section === sk);
    let sum = 0, n = 0;
    for (const it of inSection) {
      const val = items[it.key];
      if (val !== undefined && val !== null && val !== "" && val !== "NA") {
        const num = parseFloat(val);
        if (!Number.isNaN(num)) { sum += num; n++; }
      }
    }
    return { sum, n, total: inSection.length, max: inSection.length * 7 };
  };

  const blocks = Object.entries(CORQ_SECTIONS)
    .map(([sk, meta]) => {
      const inSection = CORQ_ITEMS.filter((it) => it.section === sk);
      const t = sectionTotal(sk);
      const itemsHtml = inSection
        .map((it) => {
          const val = items[it.key] !== undefined ? String(items[it.key]) : "";
          const opts = ["", "0", "1", "2", "3", "4", "5", "6", "7", "NA"].map((o) => `<option value="${o}" ${val === o ? "selected" : ""}>${o || "—"}</option>`).join("");
          const tag = it.inverse ? '<span class="corq-inverse-tag" title="Valence négative">⇣</span>' : '<span class="corq-positive-tag" title="Valence positive">⇡</span>';
          return `<div class="corq-row"><div class="corq-key">${it.key}</div><div class="corq-label">${escapeHtml(it.label)} ${tag}</div>
            <select data-fkey="items.${it.key}" ${writable ? "" : "disabled"}>${opts}</select></div>`;
        })
        .join("");
      return `<div class="corq-block" style="background:${meta.color}40;border-left:4px solid ${meta.color}">
        <h4 style="display:flex;justify-content:space-between"><span>${escapeHtml(meta.label)}${meta.inverse ? " (score bas = bon)" : " (score haut = bon)"}</span>
        <span class="corq-total-badge">Total : <strong>${t.sum}</strong> / ${t.max} (${t.n}/${t.total})</span></h4>
        ${itemsHtml}</div>`;
    })
    .join("");

  const evaVal = v.eva_cm !== undefined && v.eva_cm !== "" ? parseFloat(v.eva_cm) : 5;
  return `<p class="field-hint"><strong>Échelle CORQ-FR :</strong> 0 (aucun jour) à 7 (tous les jours) ; NA = non applicable.</p>
    ${blocks}
    <div class="corq-vas">
      <label>Score EVA — Qualité de vie globale</label>
      <div class="vas-controls">
        <span style="font-size:11px">0 cm (très mauvaise)</span>
        <input type="range" min="0" max="10" step="0.1" value="${evaVal}" data-fkey="eva_cm" ${writable ? "" : "disabled"}>
        <span style="font-size:11px">10 cm (excellente)</span>
      </div>
    </div>`;
}
